import { ref, computed } from "vue";
import filters from "@/utils/filters";
import { TaskType } from "@/utils/enums";
import { disableBeforeUnload } from "@/utils/unloadGuard";
import { getTaskTypeIcon } from "@/utils/iconMapping";
import BucketService from "@/services/bucket/bucket";

export function useBucketForm(
  props: any,
  emit: any,
  updateBucket: any,
  registerBucket: any,
  registerActivityLog: any,
  logCallBySid: any,
  isCallDispositionRequired: any,
  selectedCallDisposition: any,
  callDispositionError: any,
  mustLogAfterCall: any,
  currentCallSid: any,
  hasCallBeenLogged: any,
  isPostCallLocked: any,
  account: any,
  reactiveContact: any,
  bucketOptions: any,
  toast: any,
  router: any
) {
  const editBucketObj = ref<any>({
    id: null,
    taskType: null,
    followUpDate: null,
    bucketOptionId: null,
  });
  const selectedFollowUpOption = ref<any>(null);
  const selectedMeeting = ref<any>(null);
  const nextTaskKey = ref(0);
  const showCloseConfirmationModal = ref(false);

  const taskTypeLabelMap: Record<string, string> = {
    [TaskType.call]: "Call",
    [TaskType.email]: "Email",
    [TaskType.linkedIn]: "LinkedIn",
    [TaskType.whatsApp]: "WhatsApp",
    [TaskType.sms]: "SMS",
    [TaskType.other]: "Other",
  };

  const taskTypeOptions = computed(() =>
    Object.values(TaskType).map((task) => ({
      value: task,
      label: taskTypeLabelMap[task] || task,
      icon: getTaskTypeIcon(task),
    }))
  );

  const bucketOptionsFormatted = computed(() =>
    (bucketOptions.value || []).map((bucket: any) => ({
      value: bucket.id,
      label: bucket.name,
      icon: "fa6-solid:bucket",
      iconColor: bucket.colorHex,
    }))
  );

  const followUpOnOptions = computed(() => [
    { value: "1", label: "Follow Up Tomorrow" },
    { value: "3", label: "Follow Up In 3 Days" },
    { value: "7", label: "Follow Up In 1 Week" },
    { value: "14", label: "Follow Up In 2 Weeks" },
    { value: "30", label: "Follow Up In 1 Month" },
    { value: "60", label: "Follow Up In 2 Months" },
    { value: "90", label: "Follow Up In 3 Months" },
    { value: "180", label: "Follow Up In 6 Months" },
    { value: "365", label: "Follow Up In 1 Year" },
  ]);

  function setValues() {
    editBucketObj.value.id = props.selectedBucket?.id;
    editBucketObj.value.taskType = props.selectedBucket?.taskType;
    editBucketObj.value.followUpDate = props.selectedBucket?.followUpDate;
    editBucketObj.value.bucketOptionId = props.selectedBucket?.bucketOptionId;
  }

  function closeModal() {
    if (isPostCallLocked.value) return;

    if (mustLogAfterCall.value) {
      callDispositionError.value = "Please log the call and save disposition before closing.";
      toast.error("Please log the call and save disposition before closing.");
      return;
    }

    if (currentCallSid.value && !hasCallBeenLogged.value) {
      if (isCallDispositionRequired.value && !selectedCallDisposition.value) {
        callDispositionError.value = "Please select a call disposition before closing.";
        toast.error("Please select a call disposition before closing.");
        return;
      }
      showCloseConfirmationModal.value = true;
      return;
    }
    emit("closeTaskFollowupModal");
  }

  // App.vue feeds this modal a synthetic bucket (id: null) when opening it from a
  // notification, post-call flow, or the power dialer, since it doesn't itself know
  // whether the contact already has an active bucket/task. Sending that null id to
  // the edit endpoint fails ("Id not set"), but blindly creating instead would also
  // be wrong for a contact who already has one (duplicate task, backend rejects it,
  // Save silently fails to close) — so look it up first and update it if found.
  async function saveBucket() {
    const contactId = reactiveContact.value?.id || props.selectedBucket?.contactId;

    if (!editBucketObj.value.id && contactId && account.value?.organizationId) {
      try {
        const existing = await BucketService.getBuckets({
          organizationId: account.value.organizationId,
          contactId,
          limit: 1,
        });
        const existingBucket = existing.data?.data?.[0];
        if (existingBucket?.id) {
          editBucketObj.value.id = existingBucket.id;
        }
      } catch (error) {
        console.error("[useBucketForm] Failed to check for an existing bucket:", error);
      }
    }

    if (editBucketObj.value.id) {
      return updateBucket(editBucketObj.value);
    }
    if (!contactId) {
      toast.error("Unable to save: contact is not loaded yet. Please try again.");
      return;
    }
    const result = await registerBucket({
      taskType: editBucketObj.value.taskType,
      bucketOptionId: editBucketObj.value.bucketOptionId,
      followUpDate: editBucketObj.value.followUpDate,
      note: editBucketObj.value.note,
      contactIds: [contactId],
      organizationId: account.value?.organizationId,
    });
    // The register endpoint is bulk-shaped (contactIds array), so it may respond
    // with an array of created buckets even though we only ever send one contact.
    const createdBucket = Array.isArray(result) ? result[0] : result;
    if (createdBucket?.id) {
      editBucketObj.value.id = createdBucket.id;
    }
    return result;
  }

  async function editBucket() {
    if (isCallDispositionRequired.value && !selectedCallDisposition.value) {
      callDispositionError.value = "Please select a call disposition before saving.";
      toast.error("Please select a call disposition before saving.");
      return;
    }

    callDispositionError.value = null;

    if (currentCallSid.value && !hasCallBeenLogged.value) {
      try {
        await logCallBySid();
      } catch (error) {
        console.error("Failed to log call:", error);
      }
    }

    try {
      const result = await saveBucket();
      const succeeded = Array.isArray(result) ? result.length > 0 : !!result;
      if (succeeded) {
        if (
          props.selectedBucket?.id &&
          props.selectedBucket?.taskType !== editBucketObj.value.taskType
        ) {
          await registerActivityLog({
            taskType: editBucketObj.value.taskType,
            contactId: reactiveContact.value?.id || props.selectedBucket?.contactId,
            loggedOn: new Date(),
            organizationId: account.value?.organizationId,
          });
        }

        emit("refreshBuckets");
        closeModal();
      }
    } catch (e: any) {
      console.error("[useBucketForm] Failed to save bucket:", e);
      toast.error(e?.response?.data?.message || "Failed to save. Please try again.");
    }
  }

  async function handleSaveAndLogCall() {
    try {
      const result = await saveBucket();
      const succeeded = Array.isArray(result) ? result.length > 0 : !!result;
      if (succeeded) {
        if (
          props.selectedBucket?.id &&
          props.selectedBucket?.taskType !== editBucketObj.value.taskType
        ) {
          await registerActivityLog({
            taskType: editBucketObj.value.taskType,
            contactId: reactiveContact.value?.id || props.selectedBucket?.contactId,
            loggedOn: new Date(),
            organizationId: account.value?.organizationId,
          });
        }

        emit("refreshBuckets");
        emit("closeTaskFollowupModal");
      }
    } catch (e) {
      toast.error("Failed to save. Please try again.");
    } finally {
      showCloseConfirmationModal.value = false;
    }
  }

  function handleNextTaskSave(editBucketObjData: any) {
    editBucketObj.value = { ...editBucketObjData };
    editBucket();
  }

  function handleCancel() {
    showCloseConfirmationModal.value = false;
  }

  function handlePromptSelectDisposition() {
    showCloseConfirmationModal.value = false;
    callDispositionError.value = "Please select a call disposition before logging the call.";
    toast.error("Please select a call disposition before logging the call.");
  }

  function openReminderFollowupModal(meeting: any) {
    selectedMeeting.value = meeting;
  }

  function closeParentModal() {
    closeModal();
  }

  function closeReminderFollowupModal() {
    closeModal();
    emit("showMoveBucketModal", reactiveContact.value);
  }

  return {
    editBucketObj,
    selectedFollowUpOption,
    selectedMeeting,
    nextTaskKey,
    showCloseConfirmationModal,
    taskTypeOptions,
    bucketOptionsFormatted,
    followUpOnOptions,
    setValues,
    closeModal,
    editBucket,
    handleSaveAndLogCall,
    handleNextTaskSave,
    handleCancel,
    handlePromptSelectDisposition,
    openReminderFollowupModal,
    closeParentModal,
    closeReminderFollowupModal,
  };
}
